import { useId, useRef, useState } from "react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib";

const DESKTOP_QUERY = "(min-width: 768px)";

type DialogResponsiveProps = React.PropsWithChildren & {
  /**
   * The dialog's accessible name, in Brazilian Portuguese. Required: a dialog
   * without a title is announced as "dialog" and nothing else, and Radix warns
   * about it on every open.
   */
  title: string;
  description?: string;
  /** Decorative mark rendered before the title. */
  titleIcon?: React.ReactNode;
  /** Keeps the title as the accessible name only, for dialogs that draw their
   * own heading. */
  hideTitle?: boolean;
  button?: React.ReactElement<ButtonProps> | React.ReactNode;
  /** The caller's open state. Passing it makes the overlay controlled; leave it
   * out and the overlay keeps its own state. */
  defaultOpen?: boolean;
  className?: string;
  /** Hears every open and close, from the trigger, Escape, the "Fechar"
   * control, the backdrop or a drag, on both containers. */
  setDefaultOpen?: (open: boolean) => void;
  disable?: boolean;
};

/**
 * A record overlay: a centred dialog from 768 px, a bottom drawer below it.
 * Follows the overlay rule in `docs/architecture/design-system.md` §5
 * ("Overlays").
 *
 * - Both containers report open and close to `setDefaultOpen`.
 * - The container is chosen when the overlay opens and kept until it closes,
 *   so a rotation or a zoom change never remounts the form and drops what was
 *   typed. The next opening reads the viewport again.
 * - Opening the drawer focuses its content, never a field, so the phone
 *   keyboard stays closed. Closing returns focus to the trigger, or, for an
 *   overlay the caller opened without one, to the element that had focus when
 *   it opened.
 */
export function DialogResponsive({
  title,
  description,
  titleIcon,
  hideTitle = false,
  button,
  children,
  defaultOpen,
  setDefaultOpen,
  className,
  disable,
}: DialogResponsiveProps) {
  const controlled = defaultOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = !disable && (controlled ? defaultOpen : uncontrolledOpen);

  const matchesDesktop = useMediaQuery(DESKTOP_QUERY);
  const [isDesktop, setIsDesktop] = useState(matchesDesktop);
  if (!isOpen && isDesktop !== matchesDesktop) {
    setIsDesktop(matchesDesktop);
  }

  const triggerId = useId();
  const openerRef = useRef<HTMLElement | null>(null);

  function handleOpenChange(open: boolean) {
    if (!controlled) setUncontrolledOpen(open);
    setDefaultOpen?.(open);
  }

  function rememberOpener() {
    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  // vaul cancels Radix's auto-focus so a field never opens the phone keyboard,
  // which leaves focus on the trigger behind the sheet. Focus the content
  // itself (tabIndex -1): the keyboard stays closed and Tab stays inside.
  function focusDrawer(event: Event) {
    rememberOpener();
    event.preventDefault();
    if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus();
  }

  // Radix focuses its own trigger, which is gone when the container changed
  // after the overlay closed, and there is none when the caller opened it.
  function returnFocus(event: Event) {
    const opener = openerRef.current;
    openerRef.current = null;
    const trigger = document.querySelector<HTMLElement>(
      `[data-dialog-responsive-trigger="${triggerId}"]`
    );
    const target = trigger ?? (opener?.isConnected ? opener : null);
    if (!target) return;
    event.preventDefault();
    target.focus();
  }

  // Radix points aria-describedby at a description that does not exist unless
  // the attribute is dropped, which is what it warns about.
  const describedBy = description ? {} : { "aria-describedby": undefined };
  const contentProps = {
    ...describedBy,
    onOpenAutoFocus: rememberOpener,
    onCloseAutoFocus: returnFocus,
  };
  const triggerProps = {
    disabled: disable,
    "data-dialog-responsive-trigger": triggerId,
    asChild: true,
  };

  const headerClassName = cn(
    "flex-none px-0 pb-4 pr-12 text-left",
    hideTitle && "sr-only"
  );
  const titleClassName = "flex items-center gap-2 text-lg font-semibold text-app-fg-strong";
  const descriptionClassName = "text-sm text-app-fg-muted";

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger {...triggerProps}>{button}</DialogTrigger>
        <DialogContent className={cn(className)} {...contentProps}>
          <DialogHeader className={headerClassName}>
            <DialogTitle className={titleClassName}>
              {titleIcon}
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription className={descriptionClassName}>
                {description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger {...triggerProps}>{button}</DrawerTrigger>
      <DrawerContent
        className={cn(className)}
        {...contentProps}
        onOpenAutoFocus={focusDrawer}
      >
        <DrawerHeader className={headerClassName}>
          <DrawerTitle className={titleClassName}>
            {titleIcon}
            {title}
          </DrawerTitle>
          {description ? (
            <DrawerDescription className={descriptionClassName}>
              {description}
            </DrawerDescription>
          ) : null}
        </DrawerHeader>
        {children}
      </DrawerContent>
    </Drawer>
  );
}
